import { useState } from 'react'
import { DocumentRow } from './DocumentRow'
import type { MonthlyGroup as MonthlyGroupType } from '@/types'

interface MonthlyGroupProps {
  group: MonthlyGroupType
  onDocumentSelect: (id: string) => void
  onDocumentDownload: (s3Key: string) => void
}

// Format month for display (YYYY-MM -> "January 2025")
function formatMonth(monthStr: string): string {
  const [year, month] = monthStr.split('-')
  const date = new Date(parseInt(year), parseInt(month) - 1)
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
}

export function MonthlyGroup({
  group,
  onDocumentSelect,
  onDocumentDownload,
}: MonthlyGroupProps) {
  const [isExpanded, setIsExpanded] = useState(group.isExpanded)

  return (
    <div className="border border-gray-200 rounded-lg mb-4" data-testid={`monthly-group-${group.month}`}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-t-lg"
        data-testid={`monthly-group-toggle-${group.month}`}
      >
        <div className="flex items-center gap-4">
          <span className="text-lg font-semibold text-gray-900">
            {formatMonth(group.month)}
          </span>
          <span className="text-sm text-gray-500">
            {group.count} document{group.count !== 1 ? 's' : ''}
          </span>
        </div>

        <div className="flex items-center gap-6">
          <svg
            className={`w-5 h-5 text-gray-500 transition-transform ${
              isExpanded ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </button>

      {isExpanded && (
        <div className="overflow-x-auto">
          <table className="min-w-full table-fixed divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-[12%] px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Date
                </th>
                <th className="w-[12%] px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Number
                </th>
                <th className="w-[18%] px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Issued By
                </th>
                <th className="w-[12%] px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Value
                </th>
                <th className="w-[10%] px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Type
                </th>
                <th className="w-[12%] px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="w-[12%] px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Sumit
                </th>
                <th className="w-[12%] px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {group.documents.map((doc) => (
                <DocumentRow
                  key={doc.id}
                  document={doc}
                  onSelect={() => onDocumentSelect(doc.id)}
                  onDownload={() => onDocumentDownload(doc.s3_key)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
